
import React from "react";
import {
    Plus,
    Trash2,
    Check,
} from "lucide-react";
import {
    getDayHourlyForMonth,
    getMonthDay,
    millisecondsToHours,
    getHash,
    TaskFeildsType as BaseTaskFields,
    getNewTaskForDropOrPaste,
    useCalendarTaskContext,
    getUniqueId,
    updateOffsetWithDateCalendarForWeek,
    checkDuplicates,
} from "react-weekly-planning";
import {
    Modal,
    Form,
    Input,
    TimePicker,
    Select,
    message,
    ConfigProvider,
    Checkbox,
    Drawer,
    DatePicker,
} from "antd";


import dayjs from "dayjs";
import "antd/dist/reset.css";

import { MonthDay, Project, ProjectPriority, TaskStep, TaskPriority } from "../definition";
import VirtualItem from "./virtualItem";
import EventPill from "./EventPill";
import { useProjectContext } from "../contexts/ProjectContext";
import {
    Folder,
    Target,
    Eye,
    Edit2,
    ListTodo,
} from "lucide-react";

interface TaskFeildsType extends BaseTaskFields {
    isCompleted?: boolean;
    priority?: TaskPriority;
    steps?: TaskStep[];
}



/**
 * Main Calendar Grid component (Manages all tasks)
 */
const CalendarGrid: React.FC<{ monthOffset: number }> = ({ monthOffset }) => {

    // Retrieve task management tools from react-weekly-planning
    const { addTask, getTasks, getTask, deleteTask, updateTask } = useCalendarTaskContext();
    const { projects, addProject, updateProject, deleteProject, selectedProjectId, setSelectedProjectId } = useProjectContext();
    const today = new Date();

    // UI States (Modal, Editing, Day Selection)
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isProjectModalOpen, setIsProjectModalOpen] = React.useState(false);
    const [editingProject, setEditingProject] = React.useState<Project | null>(null);
    const [dragOverDay, setDragOverDay] = React.useState<number | null>(null);
    const [editingTask, setEditingTask] = React.useState<{ task: TaskFeildsType; hash: string } | null>(null);
    const [selectedDayInfo, setSelectedDayInfo] = React.useState<{
        day: Date;
        dayIndex: number;
        start: number;
        end: number;
    } | null>(null);
    const [form] = Form.useForm();
    const [projectForm] = Form.useForm();

    // Day data for the current month view
    const currentMonthDays: MonthDay[] = getMonthDay(monthOffset);
    const currentMonthHourly = getDayHourlyForMonth(monthOffset);

    /**
     * Opens modal to CREATE a new event on a specific day.
     */
    const handleOpenModal = (dayIndex: number) => {
        setEditingTask(null);
        const hourlyInfo = currentMonthHourly[dayIndex];
        setSelectedDayInfo({
            day: hourlyInfo.day,
            dayIndex: dayIndex,
            start: hourlyInfo.start,
            end: hourlyInfo.end,
        });

        const startHour = parseFloat(millisecondsToHours(hourlyInfo.start));
        const endHour = parseFloat(millisecondsToHours(hourlyInfo.end));

        form.setFieldsValue({
            title: "",
            timeRange: [
                dayjs().startOf("day").add(startHour, "hour"),
                dayjs().startOf("day").add(endHour, "hour"),
            ],
            color: "bg-red-50 text-red-700 border-l-4 border-l-red-400",
            projectId: selectedProjectId || projects[0]?.id,
            priority: 'medium',
            isCompleted: false,
            steps: [],
            expiryDate: dayjs(hourlyInfo.day).add(1, "day")
        });



        setIsModalOpen(true);
    };

    /**
     * Opens modal to MODIFY an existing task.
     */
    const handleEditTask = (task: TaskFeildsType, hash: string) => {
        setEditingTask({ task, hash });
        setSelectedDayInfo({
            day: task.taskDate as any,
            dayIndex: task.dayIndex,
            start: task.taskStart,
            end: task.taskEnd,
        });

        form.setFieldsValue({
            title: task.taskSummary,
            timeRange: [dayjs(task.taskStart), dayjs(task.taskEnd)],
            color: task.taskColor,
            projectId: task.groupId || selectedProjectId,
            priority: (task as TaskFeildsType).priority || 'medium',
            isCompleted: (task as TaskFeildsType).isCompleted || false,
            steps: (task as TaskFeildsType).steps || [],
            expiryDate: task.taskExpiryDate ? dayjs(task.taskExpiryDate) : dayjs(task.taskDate).add(1, 'day')
        });



        setIsModalOpen(true);
    };

    /**
     * Handles task DELETION via the modal button.
     */
    const handleDeleteTask = () => {
        if (editingTask) {
            deleteTask(editingTask.hash, editingTask.task.id);
            setIsModalOpen(false);
            message.success("Task deleted!");
        }
    };

    /**
     * Triggered when starting a task DRAG-AND-DROP.
     */
    const handleDragStart = (e: React.DragEvent, task: TaskFeildsType, hash: string) => {
        const target = e.currentTarget as HTMLElement;
        target.style.opacity = "0.4";
        // Set task data in dataTransfer (JSON format)
        e.dataTransfer.setData("application/json", JSON.stringify({
            id: task.id,
            taskStart: task.taskStart,
            taskEnd: task.taskEnd,
            dayIndex: task.dayIndex,
            hash: hash
        }));
    };

    /**
     * Resets styles and states after dragging ends.
     */
    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.currentTarget as HTMLElement;
        target.style.opacity = "1";
        setDragOverDay(null);
    };

    /**
     * Handles task DROP onto a new cell.
     */

    const handleDrop = (e: React.DragEvent, dayIndex: number, hash: string) => {
        e.preventDefault();
        setDragOverDay(null);
        const result = getNewTaskForDropOrPaste(e, dayIndex, selectedProjectId || "Default", getTask, hash);
        if (result && result.newTask) {
            const tasks = getTasks(hash);
            const isDuplicate = checkDuplicates(
                tasks.filter((t) => t.id !== result.newTask.id),
                result.newTask.taskStart,
                result.newTask.taskEnd,
                selectedProjectId || "Default"
            );

            if (isDuplicate) {
                message.warning("A task already exists in this time slot.");
                return;
            }

            deleteTask(result.newTask.draghash, result.newTask.id);
            addTask({ ...result.newTask, id: getUniqueId() });
        }
    };

    /**
     * Toggles task completion status directly from the grid.
     */
    const handleToggleCompletion = (e: React.MouseEvent, task: TaskFeildsType, hash: string) => {

        e.stopPropagation();
        const updatedTask = { ...task, isCompleted: !task.isCompleted };
        updateTask(hash, task.id, updatedTask);
        message.info(`Task marked as ${updatedTask.isCompleted ? 'completed' : 'pending'}`);
    };

    /**
     * SAVES changes (Add or Update) after form submission.
     */
    const handleSaveTask = (values: any) => {
        if (!selectedDayInfo) return;

        const { title, timeRange, projectId, priority, isCompleted, steps, expiryDate } = values;



        const [start, end] = timeRange;
        const dayBasis = dayjs(selectedDayInfo.day).startOf('day');
        const startMs = dayBasis.hour(start.hour()).minute(start.minute()).second(0).millisecond(0).valueOf();
        const endMs = dayBasis.hour(end.hour()).minute(end.minute()).second(0).millisecond(0).valueOf();

        const taskData: TaskFeildsType = {
            id: editingTask ? editingTask.task.id : getUniqueId(),
            taskSummary: title,
            taskStart: startMs,
            taskEnd: endMs,
            taskDate: selectedDayInfo.day,
            groupId: projectId,
            dayIndex: selectedDayInfo.dayIndex,
            taskColor: projects.find(p => p.id === projectId)?.color || "bg-gray-50/95 border-gray-100",
            priority,
            isCompleted,
            steps: steps || [],
            taskExpiryDate: expiryDate.valueOf() as any
        };




        const dayWeekOffset = updateOffsetWithDateCalendarForWeek(selectedDayInfo.day);
        const hash = getHash(dayWeekOffset, projectId, selectedDayInfo.dayIndex).day;
        const tasks = getTasks(hash);

        const otherTasks = editingTask ? tasks.filter(t => t.id !== editingTask.task.id) : tasks;

        // Check for duplicates before saving
        if (checkDuplicates(otherTasks, startMs, endMs, projectId)) {
            message.error("This time slot is already occupied.");
            return;
        }

        if (editingTask) {
            updateTask(editingTask.hash, editingTask.task.id, taskData);
            message.success("Task updated!");
        } else {
            addTask(taskData);
            message.success("Task added!");
        }

        setIsModalOpen(false);
        form.resetFields();
    };

    const handleSaveProject = (values: any) => {
        const priorityColors: Record<ProjectPriority, string> = {
            low: "bg-blue-50 text-blue-700 border-l-4 border-l-blue-400",
            medium: "bg-emerald-50 text-emerald-700 border-l-4 border-l-emerald-400",
            high: "bg-orange-50 text-orange-700 border-l-4 border-l-orange-400",
            critical: "bg-red-50 text-red-700 border-l-4 border-l-red-400",
        };

        const projectData = {
            ...values,
            color: priorityColors[values.priority as ProjectPriority],
        };

        if (editingProject) {
            updateProject(editingProject.id, projectData);
            message.success("Project updated!");
        } else {
            addProject(projectData);
            message.success("Project added!");
        }

        setIsProjectModalOpen(false);
        projectForm.resetFields();
    };

    const handleOpenProjectModal = (project?: Project) => {
        if (project) {
            setEditingProject(project);
            projectForm.setFieldsValue(project);
        } else {
            setEditingProject(null);
            projectForm.resetFields();
        }
        setIsProjectModalOpen(true);
    };



    return (
        <ConfigProvider theme={{ token: { colorPrimary: "#3d5a35", borderRadius: 12 } }}>
            <div className="flex gap-6 items-start pb-12">
                {/* Left Sidebar: Project Management - Sticky */}
                <div className="w-80 shrink-0 sticky top-6 max-h-[calc(100vh-120px)] overflow-hidden">

                    <div className="p-6 rounded-3xl bg-white/40 backdrop-blur-xl border border-white/40 shadow-sm flex flex-col gap-6 h-full overflow-y-auto scrollbar-hide">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-[#3d5a35] rounded-xl text-white">
                                    <Folder size={18} />
                                </div>
                                <h2 className="text-lg font-black text-gray-900">Projects</h2>
                            </div>
                            <button
                                onClick={() => handleOpenProjectModal()}
                                className="p-2 hover:bg-[#3d5a35]/10 text-[#3d5a35] rounded-xl transition-all"
                            >
                                <Plus size={20} />
                            </button>
                        </div>

                        <div className="flex flex-col gap-3">
                            {projects.map((project) => (
                                <div
                                    key={project.id}
                                    onClick={() => setSelectedProjectId(project.id)}
                                    className={`group p-4 rounded-2xl transition-all cursor-pointer border-2 flex flex-col gap-2
                                        ${selectedProjectId === project.id
                                            ? "bg-white border-[#3d5a35] shadow-md scale-[1.02]"
                                            : "bg-white/40 border-transparent hover:border-gray-200"}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-3 h-3 rounded-full ${project.color.split(' ')[2].replace('border-l-', 'bg-')}`} />
                                            <span className="font-bold text-gray-900">{project.name}</span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenProjectModal(project);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-100 rounded-lg transition-all text-gray-400"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                    </div>

                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-start gap-2 text-[11px] text-gray-500 italic">
                                            <Eye size={12} className="mt-0.5 shrink-0" />
                                            <span className="line-clamp-1">{project.vision}</span>
                                        </div>
                                        <div className="flex items-start gap-2 text-[11px] text-gray-600 font-medium">
                                            <Target size={12} className="mt-0.5 shrink-0" />
                                            <span className="line-clamp-1">{project.objective}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5 mt-1">
                                        <div className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${project.color}`}>
                                            {project.priority}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Main Calendar Grid */}
                <div className="flex-1 flex flex-col border-2 border-gray-200/60 rounded-[2.5rem] overflow-hidden bg-white/40 backdrop-blur-md shadow-inner">
                    <div className="grid grid-cols-7 flex-1">
                        {currentMonthDays.map((day, idx) => {
                            const isToday = day.dayOfTheMonth === today.getDate() &&
                                currentMonthHourly[idx].day.getMonth() === today.getMonth() &&
                                currentMonthHourly[idx].day.getFullYear() === today.getFullYear();

                            const dayWeekOffset = updateOffsetWithDateCalendarForWeek(currentMonthHourly[idx].day);
                            // Combine tasks from ALL projects for this day if no project is selected, 
                            // or fetch for the specifically selected project
                            const hashes = (selectedProjectId ? [selectedProjectId] : projects.map(p => p.id)).map(pid =>
                                getHash(dayWeekOffset, pid, idx).day
                            );

                            const allDayTasks = hashes.flatMap(hash => getTasks(hash));

                            const isFirstRow = idx < 7;
                            const isDragOver = dragOverDay === idx;

                            return (
                                <div
                                    key={day.dayOfTheMonth}
                                    onDragOver={(e) => { e.preventDefault(); setDragOverDay(idx); }}
                                    onDragLeave={() => setDragOverDay(null)}
                                    // Use first project hash for drop if none selected
                                    onDrop={(e) => handleDrop(e, idx, getHash(dayWeekOffset, selectedProjectId || projects[0].id, idx).day)}
                                    onClick={() => handleOpenModal(idx)}
                                    className={`group relative p-4 min-h-[160px] flex flex-col transition-all duration-300 border-r-2 border-b-2 border-gray-100 last:border-r-0 cursor-pointer
                      ${isToday ? "bg-[#3d5a35]/5 shadow-inner" : "bg-transparent hover:bg-white/40"}
                      ${isDragOver ? "bg-[#3d5a35]/15 scale-[1.01] z-10 shadow-lg ring-2 ring-[#3d5a35]/20 ring-inset" : ""}`}
                                >

                                    {/* Day cell header */}
                                    <div className="flex justify-between items-start mb-3 shrink-0 pointer-events-none">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-8 h-8 flex items-center justify-center text-sm font-black rounded-xl transition-all
                                       ${isToday ? "bg-[#3d5a35] text-white shadow-lg shadow-emerald-200/50" : "text-gray-900 group-hover:text-[#3d5a35]"}`}>
                                                {day.dayOfTheMonth}
                                            </span>
                                            {isFirstRow && (
                                                <span className={`text-[11px] font-black uppercase tracking-widest ${isToday ? "text-[#3d5a35]" : "text-gray-400"}`}>{day.day}</span>
                                            )}
                                        </div>

                                        <div className={`opacity-0 group-hover:opacity-100 p-1.5 rounded-lg transition-all ${isToday ? "text-[#3d5a35]" : "text-gray-400 hover:bg-[#3d5a35]/10 hover:text-[#3d5a35]"}`}>
                                            <Plus size={16} />
                                        </div>
                                    </div>

                                    {/* Day tasks list */}
                                    <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-1.5 scrollbar-hide hover:scrollbar-default">
                                        {allDayTasks.map((task) => {
                                            if (task.dayIndex !== idx) return null;
                                            const project = projects.find(p => p.id === task.groupId);
                                            const extendedTask = task as TaskFeildsType;
                                            return (
                                                <VirtualItem key={task.id}>
                                                    <EventPill
                                                        id={task.id}
                                                        title={task.taskSummary || ""}
                                                        time={`${millisecondsToHours(task.taskStart)} - ${millisecondsToHours(task.taskEnd)}`}
                                                        colorClass={project?.color || task.taskColor}
                                                        isCompleted={extendedTask.isCompleted}
                                                        priority={extendedTask.priority}
                                                        stepsCount={extendedTask.steps?.length}
                                                        completedStepsCount={extendedTask.steps?.filter(s => s.completed).length}
                                                        onDragStart={(e) => handleDragStart(e, task, getHash(dayWeekOffset, task.groupId, idx).day)}
                                                        onDragEnd={handleDragEnd}
                                                        onToggleCompletion={(e) => handleToggleCompletion(e, extendedTask, getHash(dayWeekOffset, task.groupId, idx).day)}
                                                        onClick={(e) => {
                                                            e?.stopPropagation();
                                                            handleEditTask(task, getHash(dayWeekOffset, task.groupId, idx).day);
                                                        }}
                                                    />
                                                </VirtualItem>
                                            );


                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>


            {/* Create/Edit event Drawer (Replaces Modal) */}
            <Drawer
                title={
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#3d5a35] rounded-xl flex items-center justify-center text-white shadow-lg overflow-hidden relative group">
                            <Plus size={24} strokeWidth={2.5} className="group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xl font-black text-gray-900 leading-tight">
                                {editingTask ? "Event Details" : "New Event"}
                            </span>
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                {selectedDayInfo && dayjs(selectedDayInfo.day).format("dddd, MMMM D, YYYY")}
                            </span>
                        </div>
                    </div>
                }
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                width={560}
                className="calendar-drawer"
                headerStyle={{ borderBottom: 'none', padding: '32px 32px 0 32px' }}
                bodyStyle={{ padding: '32px' }}
                contentWrapperStyle={{ borderRadius: '32px 0 0 32px', overflow: 'hidden' }}
            >
                <Form form={form} layout="vertical" onFinish={handleSaveTask} className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto pr-2 scrollbar-hide">
                        <Form.Item
                            name="title"
                            label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Main Goal / Task Name</span>}
                            rules={[{ required: true, message: "What are we aiming for?" }]}
                        >
                            <Input placeholder="e.g. Q2 Strategic Review" className="h-14 px-6 rounded-2xl border-gray-100 bg-gray-50/50 hover:bg-white focus:bg-white transition-all font-bold text-lg placeholder:text-gray-200" />
                        </Form.Item>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <Form.Item
                                name="timeRange"
                                label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Schedule</span>}
                                rules={[{ required: true, message: "Pick a time" }]}
                            >
                                <TimePicker.RangePicker
                                    format="HH:mm"
                                    className="h-14 w-full px-6 rounded-2xl border-gray-100 bg-gray-50/50 hover:bg-white focus:bg-white transition-all font-bold"
                                />
                            </Form.Item>

                            <Form.Item
                                name="expiryDate"
                                label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Expires On</span>}
                                rules={[{ required: true, message: "When does it end?" }]}
                            >
                                <DatePicker className="h-14 w-full px-6 rounded-2xl border-gray-100 bg-gray-50/50 hover:bg-white focus:bg-white transition-all font-bold" />
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="projectId"
                            label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Related Project</span>}
                            rules={[{ required: true, message: "Assign to a project" }]}
                            className="mt-4"
                        >
                            <Select className="h-14 rounded-2xl" dropdownStyle={{ borderRadius: '20px', padding: '12px' }}>
                                {projects.map((project) => (
                                    <Select.Option key={project.id} value={project.id}>
                                        <div className="flex items-center gap-3 py-1">
                                            <div className={`w-3.5 h-3.5 rounded-full ${project.color.split(' ')[2].replace('border-l-', 'bg-')}`} />
                                            <span className="font-bold text-gray-700">{project.name}</span>
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <Form.Item
                                name="priority"
                                label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Priority Level</span>}
                                initialValue="medium"
                            >
                                <Select className="h-14 rounded-2xl">
                                    <Select.Option value="low"><span className="font-bold text-blue-500">Low</span></Select.Option>
                                    <Select.Option value="medium"><span className="font-bold text-emerald-600">Medium</span></Select.Option>
                                    <Select.Option value="high"><span className="font-bold text-orange-500">High</span></Select.Option>
                                    <Select.Option value="urgent"><span className="font-bold text-red-500">Urgent</span></Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="isCompleted"
                                label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Progress State</span>}
                                valuePropName="checked"
                            >
                                <div className="h-14 flex items-center px-6 rounded-2xl border-gray-100 bg-gray-50/50">
                                    <Checkbox className="font-bold text-gray-700">Mark as Done</Checkbox>
                                </div>
                            </Form.Item>
                        </div>

                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-4 px-1">
                                <div className="flex items-center gap-2">
                                    <ListTodo size={18} className="text-[#3d5a35]" />
                                    <span className="text-sm font-black text-gray-900 uppercase tracking-widest">Task Breakdown</span>
                                </div>
                            </div>
                            <Form.List name="steps">
                                {(fields, { add, remove }) => (
                                    <div className="flex flex-col gap-4">
                                        {fields.map(({ key, name, ...restField }) => (
                                            <div key={key} className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-gray-50 shadow-sm hover:shadow-md transition-shadow group">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'completed']}
                                                    valuePropName="checked"
                                                    noStyle
                                                >
                                                    <Checkbox className="scale-125" />
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'text']}
                                                    noStyle
                                                    rules={[{ required: true, message: 'Step description' }]}
                                                >
                                                    <Input bordered={false} placeholder="What is the next step?" className="flex-1 bg-transparent font-bold text-gray-700 h-8" />
                                                </Form.Item>
                                                <button
                                                    type="button"
                                                    onClick={() => remove(name)}
                                                    className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => add({ text: '', completed: false, id: getUniqueId() })}
                                            className="w-full py-5 border-2 border-dashed border-gray-100 rounded-2xl text-gray-400 hover:text-[#3d5a35] hover:border-[#3d5a35]/30 hover:bg-emerald-50 transition-all flex items-center justify-center gap-3 group"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#3d5a35] group-hover:text-white transition-colors">
                                                <Plus size={18} />
                                            </div>
                                            <span className="text-xs font-black uppercase tracking-widest italic">Add a milestone step</span>
                                        </button>
                                    </div>
                                )}
                            </Form.List>
                        </div>
                    </div>

                    {/* Action buttons: Sticky bottom footer in drawer */}
                    <div className="flex gap-4 mt-8 pt-8 border-t border-gray-50">
                        {editingTask && (
                            <button
                                type="button"
                                onClick={handleDeleteTask}
                                className="h-16 px-8 rounded-2xl flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-100 transition-all border border-transparent hover:scale-[1.02]"
                            >
                                <Trash2 size={24} strokeWidth={2.5} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="flex-1 h-16 rounded-2xl text-sm font-black text-gray-400 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 transition-all border border-transparent"
                        >
                            Close View
                        </button>
                        <button
                            type="submit"
                            className="px-10 h-16 rounded-2xl flex items-center justify-center bg-[#3d5a35] text-white hover:bg-[#2d4627] shadow-xl shadow-emerald-200/50 transition-all hover:scale-[1.02] active:scale-95 border border-transparent gap-3"
                        >
                            <span className="font-black uppercase tracking-widest text-sm">{editingTask ? 'Apply Changes' : 'Create Task'}</span>
                            <Check size={24} strokeWidth={3} />
                        </button>
                    </div>
                </Form>
            </Drawer>


            {/* Project Creation/Edit Modal */}
            <Modal
                title={
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-[#3d5a35] rounded-xl flex items-center justify-center text-white shadow-sm">
                            <Folder size={24} strokeWidth={2.5} />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xl font-black text-gray-900 leading-tight">
                                {editingProject ? "Edit" : "New"} Project
                            </span>
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                                Project Vision & Strategy
                            </span>
                        </div>
                    </div>
                }
                open={isProjectModalOpen}
                onCancel={() => setIsProjectModalOpen(false)}
                footer={null}
                centered
                width={480}
                className="calendar-modal"
            >
                <Form form={projectForm} layout="vertical" onFinish={handleSaveProject} className="mt-8">
                    <Form.Item
                        name="name"
                        label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Project Name</span>}
                        rules={[{ required: true, message: "Enter project name" }]}
                    >
                        <Input placeholder="Operation Moonlight" className="py-3 px-4 rounded-xl border-gray-200 bg-gray-50 focus:bg-white transition-all font-bold" />
                    </Form.Item>

                    <Form.Item
                        name="vision"
                        label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Vision</span>}
                    >
                        <Input.TextArea placeholder="What is the long-term dream?" className="py-3 px-4 rounded-xl border-gray-200 bg-gray-50 focus:bg-white transition-all font-bold" />
                    </Form.Item>

                    <Form.Item
                        name="objective"
                        label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Core Objective</span>}
                    >
                        <Input placeholder="Short-term milestone" className="py-3 px-4 rounded-xl border-gray-200 bg-gray-50 focus:bg-white transition-all font-bold" />
                    </Form.Item>

                    <Form.Item
                        name="priority"
                        label={<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Priority</span>}
                        rules={[{ required: true }]}
                        initialValue="medium"
                    >
                        <Select className="h-12 rounded-xl">
                            <Select.Option value="low">Low</Select.Option>
                            <Select.Option value="medium">Medium</Select.Option>
                            <Select.Option value="high">High</Select.Option>
                            <Select.Option value="critical">Critical</Select.Option>
                        </Select>
                    </Form.Item>

                    <div className="flex gap-4 mt-12">
                        {editingProject && (
                            <button
                                type="button"
                                onClick={() => {
                                    deleteProject(editingProject.id);
                                    setIsProjectModalOpen(false);
                                    message.success("Project deleted");
                                }}
                                className="w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-100 transition-all"
                            >
                                <Trash2 size={22} strokeWidth={2.5} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsProjectModalOpen(false)}
                            className="flex-1 h-14 rounded-2xl text-sm font-black text-gray-400 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center bg-[#3d5a35] text-white hover:bg-[#2d4627] shadow-lg transition-all"
                        >
                            <Check size={24} strokeWidth={3} />
                        </button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default CalendarGrid;
